import React, { useState } from 'react';
import { LANDING_URL, AUTH_URL } from '../lib/service-registry';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface NoEmployeeRecordProps {
  userName?: string;
  userEmail?: string;
}

/**
 * TSCORP-485: Graceful fallback screen for SSO users who have no HR employee record.
 * Shown instead of the generic AccessDeniedView when the user is authenticated via
 * Platform Token but has no matching employee profile in the HR database.
 *
 * Design: GCP Material Light (#1a73e8 primary, #f8f9fa bg, white card).
 */
const NoEmployeeRecord: React.FC<NoEmployeeRecordProps> = ({ userName, userEmail }) => {
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestAccess = async () => {
    setIsLoading(true);
    setRequestError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/request-hr-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail || '', name: userName || '' }),
      });
      if (!res.ok) {
        throw new Error('요청 처리 중 오류가 발생했습니다.');
      }
      setRequestSent(true);
    } catch (err: any) {
      setRequestError(err?.message || '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: "'Inter', 'Noto Sans KR', sans-serif",
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e8eaed',
          boxShadow: '0 1px 3px rgba(60,64,67,0.12), 0 1px 2px rgba(60,64,67,0.08)',
          padding: '40px 36px',
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: '#e8f0fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            <line x1="12" y1="14" x2="12" y2="17" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#202124',
            margin: '0 0 8px',
          }}
        >
          인사 프로필 미등록
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: '14px',
            color: '#5f6368',
            lineHeight: '1.6',
            margin: '0 0 24px',
          }}
        >
          HR 시스템에 인사 프로필이 아직 등록되지 않았습니다.
          <br />
          HR 담당자에게 등록을 요청하시거나, 잠시 후 다시 시도해 주세요.
        </p>

        {/* User info card */}
        {(userName || userEmail) && (
          <div
            style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              textAlign: 'left',
            }}
          >
            <p
              style={{
                fontSize: '11px',
                color: '#80868b',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
                margin: '0 0 6px',
              }}
            >
              로그인 계정
            </p>
            {userName && (
              <p style={{ fontSize: '14px', fontWeight: 500, color: '#202124', margin: '0 0 2px' }}>
                {userName}
              </p>
            )}
            {userEmail && (
              <p style={{ fontSize: '13px', color: '#5f6368', margin: 0 }}>
                {userEmail}
              </p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {requestSent ? (
            <div
              style={{
                background: '#e6f4ea',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                color: '#1e8e3e',
                fontWeight: 500,
              }}
            >
              등록 요청이 접수되었습니다. HR 담당자가 확인 후 연락드릴 예정입니다.
            </div>
          ) : (
            <button
              onClick={handleRequestAccess}
              disabled={isLoading}
              style={{
                background: isLoading ? '#8ab4f8' : '#1a73e8',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s ease',
              }}
            >
              {isLoading ? '요청 중...' : '등록 요청 보내기'}
            </button>
          )}

          {requestError && (
            <p style={{ fontSize: '13px', color: '#d93025', margin: '0' }}>
              {requestError}
            </p>
          )}

          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'transparent',
              color: '#1a73e8',
              border: '1px solid #e8eaed',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            다시 시도
          </button>

          <button
            onClick={() => {
              // MYB-371: PT cookie is cleared by auth service signout endpoint
              window.location.href = `${AUTH_URL}/api/signout?post_logout=${LANDING_URL}/signed-out`;
            }}
            style={{
              background: 'transparent',
              color: '#d93025',
              border: '1px solid #e8eaed',
              borderRadius: '8px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            로그아웃
          </button>

          <a
            href={LANDING_URL}
            style={{
              fontSize: '14px',
              color: '#5f6368',
              textDecoration: 'none',
              paddingTop: '4px',
            }}
          >
            TSCORP 포털로 돌아가기
          </a>
        </div>
      </div>
    </div>
  );
};

export default NoEmployeeRecord;
